import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@store/authStore';
import { 
  RiArrowLeftLine, 
  RiStarLine, 
  RiStarFill, 
  RiCheckLine, 
  RiCloseLine,
  RiUserLine,
  RiBuildingLine,
  RiMailLine,
  RiPhoneLine,
  RiPlaneLine,
  RiTeamLine,
  RiCalendarLine,
  RiMoneyDollarCircleLine,
  RiFileTextLine,
  RiCheckboxCircleLine,
  RiBellLine
} from '@remixicon/react';
import { Card, CardHeader, CardContent, CardFooter } from '@components/ui/Card';
import { Avatar, AvatarImage, AvatarFallback } from '@components/ui/Avatar';
import { Badge } from '@components/ui/Badge';
import { Skeleton } from '@components/ui/Skeleton';
import Button from '@components/ui/Button';
import { FlightLeg } from '../components';
import { transformBidData } from '../utils';

/**
 * BidDetail Page
 * Detailed view of a single bid with full information and actions
 */
const BidDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Zustand stores
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();
  
  // Local state
  const [bid, setBid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(() => {
    if (!isAuthenticated && !authLoading) {
      navigate('/signin');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const loadBidDetail = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiClient.getBidById(id);

      // Transform backend data to frontend format
      const transformedBid = transformBidData(data);

      setBid(transformedBid);
    } catch (err) {
      console.error('Error loading bid detail:', err);
      setError(err.message || 'Failed to load bid details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (isAuthenticated && user && id) {
      loadBidDetail();
    }
  }, [isAuthenticated, user, id, loadBidDetail]);

  const handleBack = () => {
    navigate('/bids');
  };

  const handleNotificationClick = () => {
    // TODO: Implement notifications
    console.log('Notifications clicked');
  };

  const handleProfileClick = () => {
    navigate('/profile-setup');
  };

  const handleAccept = async () => {
    try {
      await apiClient.updateBid(id, { status: 'ACCEPTED' });
      // Reload bid to get updated data
      await loadBidDetail();
      alert('Bid accepted successfully!');
    } catch (err) {
      console.error('Error accepting bid:', err);
      alert('Failed to accept bid: ' + (err.message || 'Unknown error'));
    }
  };

  const handleReject = async () => {
    try {
      await apiClient.updateBid(id, { status: 'REJECTED' });
      // Reload bid to get updated data
      await loadBidDetail();
      alert('Bid rejected successfully!');
    } catch (err) {
      console.error('Error rejecting bid:', err);
      alert('Failed to reject bid: ' + (err.message || 'Unknown error'));
    }
  };

  const handleAddToShortlist = async () => {
    try {
      const newShortlistStatus = !bid?.isShortlisted;
      await apiClient.toggleBidShortlist(id, newShortlistStatus);
      // Reload bid to get updated data
      await loadBidDetail();
      alert(newShortlistStatus ? 'Added to shortlist!' : 'Removed from shortlist!');
    } catch (err) {
      console.error('Error toggling shortlist:', err);
      alert('Failed to update shortlist: ' + (err.message || 'Unknown error'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="overflow-hidden">
            <CardContent className="p-8 space-y-6">
              <div className="flex items-center gap-4">
                <Skeleton className="h-20 w-20 rounded-full" />
                <div className="flex-1 space-y-3">
                  <Skeleton className="h-6 w-48" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-24 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error || !bid) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="max-w-md mx-auto">
            <CardContent className="p-8 text-center space-y-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <RiCloseLine className="w-8 h-8 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {error ? 'Failed to load bid' : 'Bid not found'}
                </h3>
                <p className="text-sm text-red-600">{error || 'The bid you are looking for does not exist.'}</p>
              </div>
              <Button
                variant="primary"
                mode="filled"
                onClick={handleBack}
                className="w-full"
              >
                <RiArrowLeftLine className="w-4 h-4" />
                Back to Bids
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Modern Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top Bar */}
          <div className="flex items-center justify-between h-16">
            {/* Logo & Title */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors mr-2"
                aria-label="Go back"
              >
                <RiArrowLeftLine className="w-5 h-5" />
              </button>
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-xl">T</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">TrustJet</h1>
                <p className="text-xs text-gray-500">Bid Details</p>
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-3">
              {/* Shortlist Button */}
              <button
                onClick={handleAddToShortlist}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label={bid?.isShortlisted ? 'Remove from shortlist' : 'Add to shortlist'}
              >
                {bid?.isShortlisted ? (
                  <RiStarFill className="w-5 h-5 text-yellow-500" />
                ) : (
                  <RiStarLine className="w-5 h-5 text-gray-400" />
                )}
              </button>

              {/* Notifications */}
              <button
                onClick={handleNotificationClick}
                className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Notifications"
              >
                <RiBellLine className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>

              {/* User Profile */}
              <button
                onClick={handleProfileClick}
                className="flex items-center gap-2 p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="User profile"
              >
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-indigo-100 text-indigo-700 font-semibold text-sm">
                    {user?.email?.charAt(0).toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Client Information Card */}
        <Card className="overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <RiUserLine className="w-5 h-5 text-indigo-600" />
              Client Information
            </h2>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="relative shrink-0">
                <Avatar className="w-20 h-20 rounded-full">
                  {bid.client?.avatarUrl ? (
                    <AvatarImage src={bid.client.avatarUrl} alt={bid.client.name} />
                  ) : (
                    <AvatarFallback className="bg-indigo-100 text-indigo-700 font-semibold text-2xl">
                      {bid.client?.name?.charAt(0) || '?'}
                    </AvatarFallback>
                  )}
                </Avatar>
                {bid.client?.isVerified && (
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white">
                    <RiCheckboxCircleLine className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{bid.client?.name}</h3>
                  <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-1">
                    <RiBuildingLine className="w-4 h-4" />
                    {bid.client?.company}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <RiMailLine className="w-4 h-4 text-gray-400" />
                    <span>{bid.client?.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <RiPhoneLine className="w-4 h-4 text-gray-400" />
                    <span>{bid.client?.phone}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Flight Details Card */}
        <Card className="overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <RiPlaneLine className="w-5 h-5 text-blue-600" />
              Flight Itinerary
            </h2>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {bid.flights?.map((flight, index) => (
              <div key={flight.id} className="space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="secondary" className="text-xs">
                    Leg {index + 1}
                  </Badge>
                  {index > 0 && (
                    <span className="text-xs text-gray-500">Return Flight</span>
                  )}
                </div>
                <FlightLeg
                  origin={flight.origin}
                  destination={flight.destination}
                  duration={flight.duration}
                  date={flight.date}
                  isReturn={index > 0}
                  isLast={index === bid.flights.length - 1}
                />
                {flight.departureTime && flight.arrivalTime && (
                  <div className="flex items-center gap-4 text-xs text-gray-500 mt-2 pl-2">
                    <span className="flex items-center gap-1">
                      <RiCalendarLine className="w-3 h-3" />
                      Departure: {flight.departureTime}
                    </span>
                    <span className="flex items-center gap-1">
                      <RiCalendarLine className="w-3 h-3" />
                      Arrival: {flight.arrivalTime}
                    </span>
                  </div>
                )}
              </div>
            ))}
            <div className="flex items-center gap-2 pt-3 border-t border-gray-200 text-gray-600">
              <RiTeamLine className="w-4 h-4" />
              <span className="text-sm font-medium">
                {bid.passengers} passenger{bid.passengers !== 1 ? 's' : ''}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Bid Information Card */}
        <Card className="overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <RiFileTextLine className="w-5 h-5 text-green-600" />
              Bid Information
            </h2>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">Status</label>
                <Badge 
                  variant={bid.status === 'accepted' ? 'success' : bid.status === 'rejected' ? 'destructive' : 'default'}
                  className="text-sm px-3 py-1"
                >
                  {bid.statusText}
                </Badge>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500 flex items-center gap-1">
                  <RiMoneyDollarCircleLine className="w-4 h-4" />
                  Estimated Cost
                </label>
                <p className="text-2xl font-bold text-gray-900">
                  ${bid.estimatedCost?.toLocaleString()} <span className="text-sm font-normal text-gray-500">{bid.currency}</span>
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500 flex items-center gap-1">
                  <RiCalendarLine className="w-4 h-4" />
                  Created
                </label>
                <p className="text-sm text-gray-900">{new Date(bid.createdAt).toLocaleDateString()}</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500 flex items-center gap-1">
                  <RiCalendarLine className="w-4 h-4" />
                  Last Updated
                </label>
                <p className="text-sm text-gray-900">{new Date(bid.updatedAt).toLocaleDateString()}</p>
              </div>
            </div>
            {bid.notes && (
              <div className="space-y-2 pt-4 border-t border-gray-200">
                <label className="text-sm font-medium text-gray-500">Notes</label>
                <p className="text-sm text-gray-700 bg-gray-50 p-4 rounded-lg">{bid.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        {bid.status === 'pending' && (
          <Card className="overflow-hidden">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="primary"
                  mode="filled"
                  onClick={handleAccept}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <RiCheckLine className="w-5 h-5" />
                  Accept Bid
                </Button>
                <Button
                  variant="danger"
                  mode="outlined"
                  onClick={handleReject}
                  className="flex-1"
                >
                  <RiCloseLine className="w-5 h-5" />
                  Reject Bid
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BidDetail;